import {
  Button,
  InlineMessage,
  Loader,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@fountain/fountain-ui-components';
import { makeStyles } from '@material-ui/core';
import React, { useEffect, VFC } from 'react';
import { FormattedMessage } from 'react-intl';

import globalMessages from 'shared/global/messages';
import { trackAnalyticsEvent } from 'utils/analytics';

import { messages } from './messages';
import { ScorecardResults } from './ScorecardResults';
import { ScorecardResultsData } from './ScorecardResults/scorecardResults';
import { useStyles as useResultsStyles } from './ScorecardResults/styles';
import { useScorecardResults } from './ScorecardResults/useScorecardResults';

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: 20,
    fontWeight: theme.typography.fontWeightBold as number,
  },
}));

export interface ViewScorecardModalProps {
  applicantId: string;
  scorecardTemplateId: string;
  scorecardName: string;
  handleClose: () => void;
}

// The scorecard results modal: every evaluator's submission side by side.
// Reached from the "View scorecard" status-label action and the applicant
// history link, which hire only offers once the viewer has submitted their own
// evaluation, or for a deleted scorecard.
export const ViewScorecardModal: VFC<ViewScorecardModalProps> = ({
  applicantId,
  scorecardTemplateId,
  scorecardName,
  handleClose,
}) => {
  const classes = useStyles();
  const resultsClasses = useResultsStyles();
  const templateId = Number(scorecardTemplateId);
  const { result } = useScorecardResults(templateId, applicantId);

  const data =
    result.status === 'ready'
      ? (result.data.score_card_results as unknown as ScorecardResultsData)
      : undefined;

  useEffect(() => {
    if (!data) return;
    trackAnalyticsEvent('scorecards:results_modal_view', {
      template_id: templateId,
      evaluator_count: data.evaluations.length,
      submitted_count: data.evaluations.filter(
        evaluation => evaluation.status === 'submitted',
      ).length,
    });
    // Fires once per open: `result.status` flips to ready exactly when the
    // fetch settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.status]);

  const title = data
    ? `${data.template.name} — ${data.applicant.name}`
    : scorecardName;

  const body = () => {
    if (result.isError) {
      return (
        <InlineMessage
          type="red"
          message={<FormattedMessage {...messages.resultsLoadFailed} />}
        />
      );
    }
    if (!data) {
      return (
        <div className={resultsClasses.loading}>
          <Loader block size="2rem" />
        </div>
      );
    }
    return <ScorecardResults data={data} />;
  };

  return (
    <Modal
      data-testid="view-scorecard-modal"
      ariaLabelledBy="view-scorecard-modal-header"
      open
      onClose={handleClose}
      fullScreenOnMobile
      maxWidth={900}
    >
      <ModalHeader
        onClose={handleClose}
        ariaLabelledBy="view-scorecard-modal-header"
        showIcon={false}
      >
        <span className={classes.title}>{title}</span>
      </ModalHeader>
      <ModalContent>{body()}</ModalContent>
      <ModalFooter>
        <Button autoWidth type="primary" size="medium" onClick={handleClose}>
          <FormattedMessage {...globalMessages.done} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
